import { useState, useCallback, useRef, useEffect } from 'react';
import { supabase, invokeFunction } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { API_BASE_URL, getAuthHeaders } from '@/utils/apiConfig';
import { isRetryableReportsPostResponse } from '@/lib/reportsPostRetry';
import { pollUntilGenerated } from '@/lib/pollUntilGenerated';
import { detectEcommercePlatform } from '@/shared/connectionPlatform';

export interface ReportWarning {
  type: string;
  table?: string;
  message: string;
  connectionName?: string;
}

export type ReportGenerationReason = 'inventory_source_changed' | 'mapping_change';

/**
 * Mirrors `InventorySourceMode` in supabase/functions/_shared/skuReportGenerator.ts, which is
 * what populates `inventorySourceApplied` on the reports response. `'all'` means per-connection.
 */
export type InventorySourceApplied =
  | 'amazon'
  | 'amazon_csv'
  | 'shopify'
  | 'shopify_csv'
  | 'veeqo'
  | 'all';

type ReportsFunctionResponse = {
  success?: boolean;
  data?: unknown[];
  totalRows?: number;
  generating?: boolean;
  generationReason?: ReportGenerationReason | null;
  jobStatus?: 'queued' | 'processing';
  totalsData?: unknown;
  totalsInvalidDueToTimeout?: boolean;
  inventorySourceApplied?: InventorySourceApplied;
  code?: string;
  error?: string;
  warnings?: ReportWarning[];
};

interface ProgressiveReportData {
  data: any[];
  loading: boolean;
  generating: boolean;
  generationReason: ReportGenerationReason | null;
  loadingBySource: Record<string, boolean>;
  completedSources: string[];
  error: string | null;
  totalRows: number;
  totalRowsKnown: boolean; // Whether totalRows is accurate (false for multi-source SKU reports before count fetched)
  totalsData: any;
  totalsUnavailable: boolean;
  warnings: ReportWarning[];
  inventorySourceApplied?: InventorySourceApplied | null;
}

// SKU grouping is now handled on the backend

interface UseProgressiveReportsOptions {
  organisation: any;
  onDataUpdate?: (data: any[], totalRows?: number, totalsData?: any) => void;
}

const REPORT_RETRY_DELAYS_MS = [300, 600, 1200] as const;

// Helper to build normalized filter key for caching
const buildFilterKey = (reportType: string, filters: any): string => {
  const trailing = !!filters.trailing12Months;
  const dateKey = trailing ? 'T12M' : `${filters.startDate || ''}:${filters.endDate || ''}`;

  // Normalize column filters for stable cache key
  const cf = (filters.columnFilters || [])
    .map((f: any) => `${f.column}|${f.operation}|${f.value ?? ''}`)
    .sort();

  // Normalize data sources for stable cache key
  const sources = (filters.dataSources || []).slice().sort();

  return JSON.stringify({
    reportType,
    dateKey,
    trailing12Months: trailing,
    nevDate: filters.nevDate || '',
    metricType: filters.metricType || '',
    columnFilters: cf,
    dataSources: sources,
    page: filters.page || 1,
    pageSize: filters.pageSize || 100,
    asOfDate: filters.asOfDate || '',
  });
};

export const useProgressiveReports = ({ organisation, onDataUpdate }: UseProgressiveReportsOptions) => {
  // Ref to stabilize fetchReportData identity — prevents useEffect re-triggers
  const organisationRef = useRef(organisation);
  organisationRef.current = organisation;
  const [reportState, setReportState] = useState<ProgressiveReportData>({
    data: [],
    loading: false,
    generating: false,
    generationReason: null,
    loadingBySource: {},
    completedSources: [],
    error: null,
    totalRows: 0,
    totalRowsKnown: true,
    totalsData: null,
    totalsUnavailable: false,
    warnings: []
  });

  const { toast } = useToast();
  const currentRequestRef = useRef<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Cache to store results by source and filter combination
  // For multi-connection SKU reports, we cache with aggregated key
  const cacheRef = useRef<Map<string, {
    data: any[];
    totals: any;
    totalsInvalidDueToTimeout?: boolean;
    rowCount: number;
    filters: string;
    isAggregated?: boolean;
    warnings?: ReportWarning[];
    inventorySourceApplied?: InventorySourceApplied | null;
  }>>(new Map());

  // Polling interval ref for on-demand generation
  const generationPollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
      if (generationPollRef.current) {
        clearInterval(generationPollRef.current);
        generationPollRef.current = null;
      }
    };
  }, []);

  const markGenerationTimedOut = () => {
    setReportState(prev => ({
      ...prev,
      generating: false,
      generationReason: null,
      error: 'Report generation timed out. Please try again.',
    }));
  };

  // Nudge coordinator on polls 3 and 8 to unstick queued jobs
  const nudgeCoordinatorOnPoll = (pollNumber: number) => {
    if (pollNumber === 3 || pollNumber === 8) {
      console.log(`[useProgressiveReports] Nudging coordinator on poll #${pollNumber}`);
      invokeFunction(supabase, 'sku-report-coordinator-v3', { body: {} })
        .catch(err => console.warn('Coordinator nudge failed:', err));
    }
  };

  const reportsEndpoint = `${API_BASE_URL}/reports-data-v10`

  interface ReportsApiErrorBody {
    error?: string;
    code?: string;
  }

  const postReports = useCallback(async <TResponse = unknown>(
    body: Record<string, unknown>,
    signal?: AbortSignal
  ): Promise<TResponse> => {
    const sleepWithAbort = (ms: number) => new Promise<void>((resolve, reject) => {
      if (signal?.aborted) {
        reject(new DOMException('The operation was aborted.', 'AbortError'));
        return;
      }

      const timeoutId = setTimeout(() => {
        signal?.removeEventListener('abort', onAbort);
        resolve();
      }, ms);

      const onAbort = () => {
        clearTimeout(timeoutId);
        signal?.removeEventListener('abort', onAbort);
        reject(new DOMException('The operation was aborted.', 'AbortError'));
      };

      signal?.addEventListener('abort', onAbort, { once: true });
    });

    let lastRetryableError: Error | null = null;

    for (let attempt = 0; attempt <= REPORT_RETRY_DELAYS_MS.length; attempt += 1) {
      if (signal?.aborted) {
        throw new DOMException('The operation was aborted.', 'AbortError');
      }

      let response: Response;
      let json: unknown = {};
      try {
        // Resolved per attempt, not once before the loop: retries span several
        // seconds, so a token refreshed mid-retry must be picked up.
        const authHeaders = await getAuthHeaders();

        response = await fetch(reportsEndpoint, {
          method: 'POST',
          headers: authHeaders,
          body: JSON.stringify(body),
          signal,
        });

        json = await response.json().catch(() => ({}));
      } catch (error) {
        if (error instanceof Error && error.name === 'AbortError') {
          throw error;
        }

        // Network errors from fetch don't have status codes.
        // Retry with the same backoff window, then surface the last error.
        if (attempt === REPORT_RETRY_DELAYS_MS.length) {
          throw error;
        }

        await sleepWithAbort(REPORT_RETRY_DELAYS_MS[attempt]);
        continue;
      }

      if (response.ok) {
        return json as TResponse;
      }

      const message = (json as ReportsApiErrorBody)?.error ?? 'Request failed';
      const requestError = new Error(message);

      if (!isRetryableReportsPostResponse(response.status, json)) {
        throw requestError;
      }

      lastRetryableError = requestError;
      if (attempt === REPORT_RETRY_DELAYS_MS.length) {
        throw lastRetryableError;
      }

      await sleepWithAbort(REPORT_RETRY_DELAYS_MS[attempt]);
    }

    throw lastRetryableError ?? new Error('Request failed');
  }, [reportsEndpoint]);

  // Helper function to fetch aggregated SKU data from multiple connections in a single request
  const fetchAggregatedSkuData = async (
    reportType: string,
    filters: any,
    dataSources: string[],
    signal: AbortSignal | undefined,
    usePrecomputed: boolean = true
  ) => {
    const baseBody = {
      reportType,
      organisationId: organisationRef.current?.id,
      filters: {
        ...filters,
        dataSource: dataSources.length === 0 ? 'All' : undefined,
        dataSources: dataSources.length > 0 ? dataSources : undefined
        // NO connectionId - this triggers two-phase query in backend
      }
    };

    // Multi-source data mode already computes totals before pagination.
    // A second mode:'totals' request would reload every SKU into the isolate.
    const dataResponse = await postReports<ReportsFunctionResponse>(
      { ...baseBody, mode: 'data', usePrecomputed },
      signal,
    );

    const totalsUnavailable = dataResponse?.code === 'WORKER_RESOURCE_LIMIT'
      || dataResponse?.success === false
      || dataResponse?.totalsInvalidDueToTimeout === true;
    if (totalsUnavailable && dataResponse?.code === 'WORKER_RESOURCE_LIMIT') {
      console.warn('[useProgressiveReports] Totals resource-limited on data response, suppressing summary row');
    }

    const generationReason: ReportGenerationReason | null =
      dataResponse?.generationReason === 'inventory_source_changed'
      || dataResponse?.generationReason === 'mapping_change'
        ? dataResponse.generationReason
        : null;

    return {
      success: dataResponse?.success ?? true,
      data: dataResponse?.data || [],
      totalRows: dataResponse?.totalRows || 0,
      totals: totalsUnavailable ? null : (dataResponse?.totalsData || null),
      totalsInvalidDueToTimeout: dataResponse?.totalsInvalidDueToTimeout === true || totalsUnavailable,
      inventorySourceApplied: dataResponse?.inventorySourceApplied ?? 'all',
      warnings: dataResponse?.warnings || [],
      generating: dataResponse?.generating === true,
      generationReason,
      jobStatus: (dataResponse?.jobStatus ?? 'queued') as 'queued' | 'processing',
      error: dataResponse?.error
    };
  };

  const fetchReportData = useCallback(async (reportType: string, filters: any) => {
    const org = organisationRef.current;
    if (!org?.connections || Object.keys(org.connections).length === 0) {
      toast({
        title: 'No Connections',
        description: 'No data connections available for reports',
        variant: 'destructive'
      });
      return;
    }

    // Abort any previous request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Clear any existing generation poll to prevent stacked intervals
    if (generationPollRef.current) {
      clearInterval(generationPollRef.current);
      generationPollRef.current = null;
    }

    // Create new AbortController for this request
    abortControllerRef.current = new AbortController();

    // Generate unique request ID to handle concurrent requests
    const requestId = `${reportType}-${Date.now()}`;
    currentRequestRef.current = requestId;

    // Create normalized filter key for cache comparison
    const filterKey = buildFilterKey(reportType, filters);

    // Reset state for new request, but preserve cached data
    setReportState({
      data: [],
      loading: true,
      generating: false,
      generationReason: null,
      loadingBySource: {},
      completedSources: [],
      error: null,
      totalRows: 0,
      totalRowsKnown: true,
      totalsData: null,
      totalsUnavailable: false,
      warnings: []
    });

    try {
      // Get available data sources
      const connections = Array.isArray(org.connections)
        ? org.connections
        : Object.values(org.connections);

      const supportedConnections = connections.filter((conn: any) => {
        // Note: broken/error connections are kept — their synced data is still queryable
        const id = (conn.connectionId || conn.id || '');
        const isCsvEcommerce = id.startsWith('csv-import-')
          && conn.import_type === 'ecommerce'
          && (conn.ecommerce_subtype === 'amazon' || conn.ecommerce_subtype === 'shopify');
        const platform = detectEcommercePlatform(conn);
        // Veeqo is intentionally excluded (see detectEcommercePlatform's doc comment): it's a
        // pooled inventory-overlay source, not an independently-reportable connection.
        return platform === 'shopify' || platform === 'amazon' || isCsvEcommerce;
      });

      // Determine which connections to fetch from
      let connectionsToFetch: any[] = [];
      const dataSources = filters.dataSources || (filters.dataSource === 'All' ? [] : [filters.dataSource]);

      if (dataSources.length === 0 || (filters.dataSource === 'All' && !filters.dataSources)) {
        // Fetch from all supported connections
        connectionsToFetch = supportedConnections;
      } else {
        // Fetch from specific connections
        connectionsToFetch = supportedConnections.filter((conn: any) =>
          dataSources.includes(conn.id) || dataSources.includes(conn.connectionId)
        );
      }

      if (connectionsToFetch.length === 0) {
        setReportState({
          data: [],
          loading: false,
          generating: false,
          generationReason: null,
          loadingBySource: {},
          completedSources: [],
          error: null,
          totalRows: 0,
          totalRowsKnown: true,
          totalsData: null,
          totalsUnavailable: false,
          warnings: []
        });
        return;
      }

      // Detect multi-source SKU reports - these need single aggregated request
      const isMultiSourceSkuReport = reportType === 'skus' && connectionsToFetch.length > 1;

      // === PATH A: Single Aggregated Request for Multi-Connection SKU Reports ===
      if (isMultiSourceSkuReport) {
        const aggregatedCacheKey = `aggregated-${reportType}-${filterKey}`;
        const cachedAggregated = cacheRef.current.get(aggregatedCacheKey);

        // Check aggregated cache first
        if (
          cachedAggregated
          && cachedAggregated.filters === filterKey
          && cachedAggregated.isAggregated
          && cachedAggregated.totalsInvalidDueToTimeout !== true
        ) {
          console.log('[useProgressiveReports] Using cached aggregated data');
          setReportState({
            data: cachedAggregated.data,
            loading: false,
            generating: false,
            generationReason: null,
            loadingBySource: {},
            completedSources: ['aggregated'],
            error: null,
            totalRows: cachedAggregated.rowCount,
            totalRowsKnown: true,
            totalsData: cachedAggregated.totals,
            totalsUnavailable: false,
            warnings: cachedAggregated.warnings || [],
            inventorySourceApplied: cachedAggregated.inventorySourceApplied ?? 'all',
          });
          return;
        }

        // Set loading state for aggregated request
        setReportState({
          data: [],
          loading: true,
          generating: false,
          generationReason: null,
          loadingBySource: { 'aggregated': true },
          completedSources: [],
          error: null,
          totalRows: 0,
          totalRowsKnown: false,
          totalsData: null,
          totalsUnavailable: false,
          warnings: []
        });

        try {
          console.log('[useProgressiveReports] Fetching aggregated SKU data from multiple sources');
          const signal = abortControllerRef.current?.signal;
          const result = await fetchAggregatedSkuData(reportType, filters, dataSources, signal);

          // Check if this request is still current
          if (currentRequestRef.current !== requestId) {
            console.log('[useProgressiveReports] Request superseded, ignoring aggregated result');
            return;
          }

          if (!result.success) {
            throw new Error(result.error || 'Failed to fetch aggregated data');
          }

          // Handle on-demand generation: backend reports data is being prepared
          if (result.generating) {
            // Strict correctness mode: do not render stale/partial aggregated rows.
            // Wait until generating=false before showing data/counts.
            console.log('[useProgressiveReports] Backend is generating report data, starting poll...', { reason: result.generationReason });
            setReportState(prev => ({
              ...prev,
              data: [],
              loading: false,
              generating: true,
              generationReason: result.generationReason ?? null,
              totalRows: 0,
              totalRowsKnown: false,
            }));

            const applyAggregatedResult = (aggregatedResult: typeof result) => {
              const resultData = aggregatedResult.data || [];
              const resultTotals = aggregatedResult.totalsInvalidDueToTimeout ? null : (aggregatedResult.totals || null);
              const resultRowCount = aggregatedResult.totalRows || resultData.length;
              const resultWarnings = aggregatedResult.warnings || [];
              setReportState({
                data: resultData,
                loading: false,
                generating: false,
                generationReason: null,
                loadingBySource: {},
                completedSources: ['aggregated'],
                error: null,
                totalRows: resultRowCount,
                totalRowsKnown: true,
                totalsData: resultTotals,
                totalsUnavailable: aggregatedResult.totalsInvalidDueToTimeout === true,
                warnings: resultWarnings,
              });
              return { resultData, resultTotals, resultRowCount, resultWarnings };
            };

            // 60 × 10s = 10 min absolute ceiling while queued; 12 × 10s = 120s once processing starts
            pollUntilGenerated({
              timerRef: generationPollRef,
              intervalMs: 10000,
              maxQueuedPolls: 60,
              maxProcessingPolls: 12,
              isCurrent: () => currentRequestRef.current === requestId,
              onTick: nudgeCoordinatorOnPoll,
              poll: (pollNumber) => {
                console.log(`[useProgressiveReports] Poll #${pollNumber} for generated data`);
                return fetchAggregatedSkuData(reportType, filters, dataSources, undefined);
              },
              classify: (pollResult) => (
                pollResult.generating
                  ? { kind: 'generating', processing: pollResult.jobStatus === 'processing' }
                  : { kind: 'done' }
              ),
              onGenerating: (pollResult) => {
                // Keep generationReason in sync with backend on every poll so the
                // banner reflects the most recent staleness cause.
                setReportState(prev => (
                  prev.generationReason === (pollResult.generationReason ?? null)
                    ? prev
                    : { ...prev, generationReason: pollResult.generationReason ?? null }
                ));
              },
              onDone: (pollResult) => {
                if (!pollResult.success) {
                  setReportState(prev => ({
                    ...prev,
                    generating: false,
                    generationReason: null,
                    error: pollResult.error || 'Failed to fetch report data after generation',
                  }));
                  return;
                }

                const { resultData, resultTotals, resultRowCount, resultWarnings } = applyAggregatedResult(pollResult);

                cacheRef.current.set(aggregatedCacheKey, {
                  data: resultData,
                  totals: resultTotals,
                  totalsInvalidDueToTimeout: pollResult.totalsInvalidDueToTimeout === true,
                  rowCount: resultRowCount,
                  filters: filterKey,
                  isAggregated: true,
                  warnings: resultWarnings,
                });

                console.log('[useProgressiveReports] Generation complete, data loaded:', {
                  rows: resultRowCount,
                  skus: resultData.length,
                });
              },
              finalFetch: async () => {
                console.log('[useProgressiveReports] Max polls reached, doing final fetch attempt...');
                const finalResult = await fetchAggregatedSkuData(reportType, filters, dataSources, undefined);
                if (finalResult.generating) return false;
                console.log('[useProgressiveReports] Final fetch succeeded with data!');
                applyAggregatedResult(finalResult);
                return true;
              },
              onTimeout: markGenerationTimedOut,
              onPollError: (pollError) => console.error('[useProgressiveReports] Poll error:', pollError),
              onFinalFetchError: (e) => console.warn('[useProgressiveReports] Final fetch attempt failed:', e),
            });

            return; // Exit, polling will handle the rest
          }

          const aggregatedData = result.data || [];
          const aggregatedTotals = result.totalsInvalidDueToTimeout ? null : (result.totals || null);
          const aggregatedRowCount = result.totalRows || aggregatedData.length;
          const aggregatedWarnings = result.warnings || [];

          // Update state with complete aggregated data
          setReportState({
            data: aggregatedData,
            loading: false,
            generating: false,
            generationReason: null,
            loadingBySource: {},
            completedSources: ['aggregated'],
            error: null,
            totalRows: aggregatedRowCount,
            totalRowsKnown: true,
            totalsData: aggregatedTotals,
            totalsUnavailable: result.totalsInvalidDueToTimeout === true,
            warnings: aggregatedWarnings,
            inventorySourceApplied: result.inventorySourceApplied ?? 'all',
          });

          // Cache aggregated result
          cacheRef.current.set(aggregatedCacheKey, {
            data: aggregatedData,
            totals: aggregatedTotals,
            totalsInvalidDueToTimeout: result.totalsInvalidDueToTimeout === true,
            rowCount: aggregatedRowCount,
            filters: filterKey,
            isAggregated: true,
            warnings: aggregatedWarnings,
            inventorySourceApplied: result.inventorySourceApplied ?? 'all',
          });

          console.log('[useProgressiveReports] Aggregated data fetch complete:', {
            rows: aggregatedRowCount,
            skus: aggregatedData.length
          });

        } catch (error: any) {
          if (error.name === 'AbortError') {
            console.log('[useProgressiveReports] Aggregated request aborted');
            return;
          }

          console.warn('[useProgressiveReports] Aggregated request failed, falling back to per-connection requests:', error.message);
          // Don't return — fall through to Path B (progressive per-connection)
        }

        // If aggregated path succeeded, exit early
        // If aggregated path succeeded (data was cached), exit early
        if (cacheRef.current.has(`aggregated-${reportType}-${filterKey}`)) {
          return;
        }

        // Fall through to Path B below
        console.log('[useProgressiveReports] Falling back to per-connection requests (Path B)');
      }

      // === PATH B: Progressive Per-Connection Requests (existing logic) ===
      // This is used for single-connection SKU reports and all non-SKU reports
      const countPromise: Promise<number | null> | null = null;

      // Separate connections into cached and uncached
      const cachedConnections: any[] = [];
      const uncachedConnections: any[] = [];
      const cachedData: any[] = [];
      const cachedTotals: any[] = [];
      let cachedRowCount = 0;

      connectionsToFetch.forEach((conn: any) => {
        const connId = conn.id || conn.connectionId;
        const cacheEntry = cacheRef.current.get(connId);

        // Check if cache is valid (same filters)
        if (cacheEntry && cacheEntry.filters === filterKey) {
          cachedConnections.push(conn);
          cachedData.push(...cacheEntry.data);
          if (cacheEntry.totals) {
            cachedTotals.push(cacheEntry.totals);
          }
          cachedRowCount += cacheEntry.rowCount;
        } else {
          uncachedConnections.push(conn);
        }
      });

      // Set up loading states only for uncached connections
      const loadingBySource: Record<string, boolean> = {};
      uncachedConnections.forEach((conn: any) => {
        const connId = conn.id || conn.connectionId;
        loadingBySource[connId] = true;
      });

      // Immediately show cached data
      if (cachedConnections.length > 0) {
        const pageSize = filters.pageSize || 100;
        const limitedCachedData = cachedData.slice(0, pageSize);
        const aggregatedCachedTotals = cachedTotals.length > 0 ? aggregateMultiConnectionTotals(cachedTotals, filters) : null;

        // Initially show cached data without accurate count (will update when count arrives)
        setReportState(prev => ({
          ...prev,
          data: limitedCachedData,
          totalRows: cachedRowCount,
          totalRowsKnown: false, // Mark as unknown until count arrives
          totalsData: uncachedConnections.length === 0 ? aggregatedCachedTotals : null, // Use cached totals when fully cached; otherwise backend will populate
          completedSources: cachedConnections.map(c => c.id || c.connectionId),
          loading: uncachedConnections.length > 0,
          loadingBySource
        }));
      } else {
        setReportState(prev => ({
          ...prev,
          loadingBySource
        }));
      }

      // If all connections are cached, we're done
      if (uncachedConnections.length === 0) {
        return;
      }

      // Fetch data only for uncached connections
      if (uncachedConnections.length > 1) {
        await fetchProgressiveData(reportType, filters, uncachedConnections, requestId, filterKey, cachedData, cachedTotals, cachedRowCount, null, isMultiSourceSkuReport);
      } else if (uncachedConnections.length === 1) {
        await fetchSingleSource(reportType, filters, uncachedConnections[0], requestId, filterKey, cachedData, cachedTotals, cachedRowCount, null, isMultiSourceSkuReport);
      }

      // Update state when count arrives (non-blocking)
      if (countPromise) {
        countPromise.then(accurateTotalCount => {
          if (accurateTotalCount !== null) {
            setReportState(prev => ({
              ...prev,
              totalRows: accurateTotalCount,
              totalRowsKnown: true
            }));
          }
        });
      }

    } catch (error) {
      // Check if error is due to abort
      if (error instanceof Error && error.name === 'AbortError') {
        return; // Don't show error for aborted requests
      }

      if (currentRequestRef.current === requestId) {
        console.error('Progressive report error:', error);
        setReportState(prev => ({
          ...prev,
          loading: false,
          error: error instanceof Error ? error.message : 'Failed to fetch report data'
        }));

        toast({
          title: 'Data Fetch Error',
          description: error instanceof Error ? error.message : 'Failed to fetch report data',
          variant: 'destructive'
        });
      }
    }
  }, [toast]);

  const fetchProgressiveData = async (
    reportType: string,
    filters: any,
    connections: any[],
    requestId: string,
    filterKey: string,
    cachedData: any[] = [],
    cachedTotals: any[] = [],
    cachedRowCount: number = 0,
    accurateTotalCount: number | null = null,
    isMultiSourceSkuReport: boolean = false
  ) => {
    // Extract dataSources from filters
    const dataSources = filters.dataSources || (filters.dataSource === 'All' ? [] : [filters.dataSource]);

    // Get connection IDs and their types for source tracking
    const connectionSources: Record<string, string> = {};
    connections.forEach((conn: any) => {
      const connId = conn.id || conn.connectionId;
      const id = (conn.connectionId || conn.id || '');
      if (id.startsWith('csv-import-') && conn.import_type === 'ecommerce') {
        connectionSources[connId] = conn.ecommerce_subtype === 'shopify' ? 'Shopify' : 'Amazon';
      } else {
        connectionSources[connId] = detectEcommercePlatform(conn) === 'shopify' ? 'Shopify' : 'Amazon';
      }
    });

    // Calculate page size per connection to respect total limit
    const pageSize = filters.pageSize || 100;
    const pageSizePerConnection = Math.ceil(pageSize / connections.length);

    // Create promises for data (one per connection) and one totals request for all connections
    const signal = abortControllerRef.current?.signal;

    // Resolved once and shared by every request below — these all fire in
    // parallel, so per-iteration resolution would add nothing but overhead.
    const authHeaders = await getAuthHeaders();

    // Data requests - one per connection for progressive loading
    const dataPromises = connections.map(conn => {
      const connId = conn.id || conn.connectionId;
      const sourceType = connectionSources[connId];

      return fetch(reportsEndpoint, {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify({
          reportType,
          organisationId: organisationRef.current?.id,
          mode: 'data',
          filters: {
            ...filters,
            dataSource: sourceType,
            connectionId: connId,
            pageSize: pageSizePerConnection
          }
        }),
        signal
      })
        .then(async (response) => {
          const data = await response.json();
          return { type: 'data', connectionId: connId, source: sourceType, data, error: !response.ok ? new Error(data.error || 'Request failed') : null };
        })
        .catch(error => ({ type: 'data', connectionId: connId, source: sourceType, data: null, error }));
    });

    // Multi-source SKU totals come from the aggregated data request. Do not
    // replay the full catalog pipeline here on the Path B fallback.
    const skipSkuTotals = reportType === 'skus' && isMultiSourceSkuReport;

    const totalsPromise = skipSkuTotals
      ? null
      : fetch(reportsEndpoint, {
          method: 'POST',
          headers: authHeaders,
          body: JSON.stringify({
            reportType,
            organisationId: organisationRef.current?.id,
            mode: 'totals',
            filters: {
              ...filters,
              dataSource: dataSources.length === 0 ? 'All' : filters.dataSource,
              dataSources: dataSources.length > 0 ? dataSources : undefined
              // No connectionId - let backend aggregate all connections
            }
          }),
          signal
        })
          .then(async (response) => {
            const data = await response.json();
            return { type: 'totals', data, error: !response.ok ? new Error(data.error || 'Request failed') : null };
          })
          .catch(error => ({ type: 'totals', data: null, error }));

    const connectionPromises = totalsPromise ? [...dataPromises, totalsPromise] : dataPromises;

    // Process results incrementally as they complete
    // Start with cached data
    const connectionData: Record<string, any[]> = {};
    const connectionTotals: Record<string, any> = {};
    const connectionRowCounts: Record<string, number> = {};
    if (skipSkuTotals) {
      // Do not let the table sum the visible page as a stand-in footer.
      connectionTotals['__aggregated__'] = null;
      connectionTotals['__totalsError__'] = true;
    }

    // Pre-populate with cached data (this won't be in the connections list since we're only fetching uncached)
    // We'll merge this in the updateStateWithLatestData function

    // Helper function to update state when new data arrives
    const updateStateWithLatestData = () => {
      if (currentRequestRef.current !== requestId) return;

      // Combine cached and newly fetched data
      const allData = [...cachedData, ...Object.values(connectionData).flat()];
      const limitedData = allData.slice(0, pageSize);

      // When column filters are applied, use actual data length
      const hasColumnFilters = filters.columnFilters && filters.columnFilters.length > 0;

      // Use accurate count if available, otherwise calculate from data
      const totalRows = hasColumnFilters
        ? allData.length  // Use actual filtered data length when filters are active
        : (accurateTotalCount !== null
          ? accurateTotalCount
          : cachedRowCount + Object.values(connectionRowCounts).reduce((sum, n) => sum + (typeof n === 'number' ? n : 0), 0) || 0);

      // Use aggregated totals directly (no need to aggregate on frontend anymore)
      const aggregatedTotals = connectionTotals['__aggregated__'] || null;
      const hasTotals = '__aggregated__' in connectionTotals;

      setReportState(prev => {
        // Update loading states - mark connections as complete when we have their data
        // Totals loading is tracked separately since it's a single aggregated call
        const updatedLoadingBySource = { ...prev.loadingBySource };
        Object.keys(connectionData).forEach(connId => {
          updatedLoadingBySource[connId] = false;
        });

        const allConnectionsComplete = Object.values(updatedLoadingBySource).every(loading => !loading) &&
          (skipSkuTotals || connectionTotals['__aggregated__'] !== undefined);

        return {
          ...prev,
          data: limitedData,
          loading: !allConnectionsComplete,
          loadingBySource: updatedLoadingBySource,
          completedSources: Object.keys(connectionData),
          totalRows,
          totalRowsKnown: accurateTotalCount !== null || !isMultiSourceSkuReport,
          totalsData: hasTotals ? aggregatedTotals : null,
          totalsUnavailable: hasTotals && connectionTotals['__totalsError__'] === true,
        };
      });

      onDataUpdate?.(limitedData, totalRows, aggregatedTotals);
    };

    // Process each promise as it resolves
    connectionPromises.forEach((promise) => {
      promise.then((result) => {
        const { type, data, error } = result;
        const connectionId = 'connectionId' in result ? result.connectionId as string : null;
        const source = 'source' in result ? result.source as string : 'All';

        // Check if this request is still current before processing
        if (currentRequestRef.current !== requestId) {
          return;
        }

        if (error) {
          // Don't log abort errors
          if (error.name === 'AbortError') {
            return;
          }
          console.error(`Error fetching ${source}${connectionId ? ` (${connectionId})` : ''} ${type}:`, error);
          if (connectionId) {
            setReportState(prev => ({
              ...prev,
              loadingBySource: { ...prev.loadingBySource, [connectionId]: false },
              error: `Failed to load ${source} data: ${error.message || 'Unknown error'}`
            }));
          } else {
            setReportState(prev => ({
              ...prev,
              error: `Failed to load totals: ${error.message || 'Unknown error'}`
            }));
          }
          return;
        }

        if (data?.success) {
          if (type === 'data' && connectionId) {
            connectionData[connectionId] = data.data || [];
            connectionRowCounts[connectionId] = data.totalRows || 0;

            // Cache the data
            const existingCache = cacheRef.current.get(connectionId) || { data: [], totals: null, rowCount: 0, filters: filterKey };
            cacheRef.current.set(connectionId, {
              ...existingCache,
              data: data.data || [],
              rowCount: data.totalRows || 0,
              filters: filterKey
            });
          } else if (type === 'totals') {
            const isResourceLimit = data?.code === 'WORKER_RESOURCE_LIMIT';
            connectionTotals['__aggregated__'] = (data?.totalsInvalidDueToTimeout || isResourceLimit)
              ? null
              : (data?.totalsData || null);
            if (isResourceLimit || data?.totalsInvalidDueToTimeout) {
              connectionTotals['__totalsError__'] = true;
            }
          }

          // Update UI immediately when new data arrives
          updateStateWithLatestData();
        }

        // Handle totals failure OUTSIDE the success guard — any !success response
        // (WORKER_RESOURCE_LIMIT, generic errors, etc.) should set the error flag
        if (!data?.success && type === 'totals') {
          connectionTotals['__aggregated__'] = null;
          connectionTotals['__totalsError__'] = true;
          updateStateWithLatestData();
        }
      }).catch(error => {
        // Don't log abort errors
        if (error.name === 'AbortError') {
          return;
        }
        if (currentRequestRef.current !== requestId) {
          return;
        }
        console.error(`Promise failed:`, error);
      });
    });

    // Wait for all promises to settle, but UI updates happen incrementally above
    await Promise.allSettled(connectionPromises);
  };

  // Helper function to aggregate totals from multiple sources
  const aggregateMultiConnectionTotals = (totalsArray: any[], filters?: any): any => {
    if (!totalsArray || totalsArray.length === 0) return null;
    if (totalsArray.length === 1) return totalsArray[0];

    const aggregated: any = {};

    // Field mapping to handle different naming conventions
    const fieldMapping = [
      { output: 'Total GMV $', inputs: ['Total GMV $', 'Total GMV'] },
      { output: 'Total NMV $', inputs: ['Total NMV $', 'Total NMV'] },
      { output: 'Total Fee Allocation $', inputs: ['Total Fee Allocation $', 'Fee Allocation $'] },
      { output: 'Total Units Sold', inputs: ['Total Units Sold'] },
      { output: 'Total Stock Held', inputs: ['Total Stock Held'] },
      { output: 'Total GEV $', inputs: ['Total GEV $', 'Total GEV'] },
      { output: 'Total NEV $', inputs: ['Total NEV $', 'Total NEV'] }
    ];

    // Sum numeric totals using field mapping
    fieldMapping.forEach(({ output, inputs }) => {
      const sum = totalsArray.reduce((total, item) => {
        // Try all possible input field names for this output field
        let value = 0;
        for (const inputField of inputs) {
          if (item[inputField] !== undefined && item[inputField] !== null) {
            value = typeof item[inputField] === 'number' ? item[inputField] :
              parseFloat(String(item[inputField] || '0').replace(/,/g, ''));
            break; // Use the first matching field
          }
        }
        return total + (isNaN(value) ? 0 : value);
      }, 0);
      aggregated[output] = sum;
    });

    // Recompute averages
    const totalUnits = aggregated['Total Units Sold'] || 1;
    aggregated['AVG GMV $'] = (aggregated['Total GMV $'] || 0) / totalUnits;
    aggregated['AVG NMV $'] = (aggregated['Total NMV $'] || 0) / totalUnits;

    // Recompute Days Until Stockout using actual date range
    const totalStock = aggregated['Total Stock Held'] || 0;
    const startDate = filters?.startDate;
    const endDate = filters?.endDate;

    // Calculate actual days in period
    let daysInPeriod = 365; // default
    if (startDate && endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);
      daysInPeriod = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1;
    }

    if (totalStock === 0) {
      aggregated['Days Until Stockout'] = 0;
    } else if (totalUnits > 0) {
      aggregated['Days Until Stockout'] = (totalStock * daysInPeriod) / totalUnits;
    } else {
      aggregated['Days Until Stockout'] = null;
    }

    return aggregated;
  };

  const fetchSingleSource = async (
    reportType: string,
    filters: any,
    connection: any,
    requestId: string,
    filterKey: string,
    cachedData: any[] = [],
    cachedTotals: any[] = [],
    cachedRowCount: number = 0,
    accurateTotalCount: number | null = null,
    isMultiSourceSkuReport: boolean = false
  ) => {
    const connId = connection.id || connection.connectionId;
    const id = (connection.connectionId || connection.id || '');
    // Veeqo is a pooled inventory-overlay source, not an independently-reportable
    // connection (see the supportedConnections filter above) -- it never reaches here.
    const sourceType = id.startsWith('csv-import-') && connection.import_type === 'ecommerce'
      ? (connection.ecommerce_subtype === 'shopify' ? 'Shopify' : 'Amazon')
      : (detectEcommercePlatform(connection) === 'shopify' ? 'Shopify' : 'Amazon');

    // For single source, still use separate data and totals calls for consistency
    const signal = abortControllerRef.current?.signal;
    const [dataResult, totalsResult] = await Promise.allSettled([
      postReports<ReportsFunctionResponse>({
        reportType,
        organisationId: organisationRef.current?.id,
        mode: 'data',
        filters: {
          ...filters,
          dataSource: sourceType,
          connectionId: connId
        }
      }, signal),
      postReports<ReportsFunctionResponse>({
        reportType,
        organisationId: organisationRef.current?.id,
        mode: 'totals',
        filters: {
          ...filters,
          dataSource: sourceType,
          connectionId: connId
        }
      }, signal)
    ]);

    // Check if this request is still current before processing results
    if (currentRequestRef.current !== requestId) {
      return;
    }
    let data: any[] = [];
    let totalRows = 0;
    let totalsData = null;
    let inventorySourceApplied: InventorySourceApplied = 'all';

    // Process data result - always extract data even if generating
    let isGenerating = false;
    if (dataResult.status === 'fulfilled' && dataResult.value?.success) {
      data = dataResult.value.data || [];
      totalRows = dataResult.value.totalRows || 0;
      inventorySourceApplied = dataResult.value.inventorySourceApplied ?? 'all';
      if (dataResult.value.generating) {
        isGenerating = true;
      }
    }

    // Also check totals result for generating flag
    if (totalsResult.status === 'fulfilled' && totalsResult.value?.generating) {
      isGenerating = true;
    }

    if (isGenerating) {
      // Pull generationReason from whichever response provided the generating signal.
      const dataReason = dataResult.status === 'fulfilled' ? dataResult.value?.generationReason ?? null : null;
      const totalsReason = totalsResult.status === 'fulfilled' ? totalsResult.value?.generationReason ?? null : null;
      const initialGenerationReason: ReportGenerationReason | null = (
        dataReason === 'inventory_source_changed' || totalsReason === 'inventory_source_changed'
      )
        ? 'inventory_source_changed'
        : (dataReason ?? totalsReason ?? null);

      console.log('[useProgressiveReports] Single source: backend is generating, waiting for fresh data (stale data suppressed)', { reason: initialGenerationReason });
      setReportState(prev => ({
        ...prev,
        loading: false,
        generating: true,
        generationReason: initialGenerationReason,
      }));

      const fetchSingleSourceBoth = () => {
        const pollFilterBody = {
          ...filters,
          dataSource: sourceType,
          connectionId: connId
        };
        const pollSignal = abortControllerRef.current?.signal;
        return Promise.allSettled([
          postReports<ReportsFunctionResponse>({
            reportType,
            organisationId: organisationRef.current?.id,
            mode: 'data',
            filters: pollFilterBody
          }, pollSignal),
          postReports<ReportsFunctionResponse>({
            reportType,
            organisationId: organisationRef.current?.id,
            mode: 'totals',
            filters: pollFilterBody
          }, pollSignal),
        ]);
      };

      const resolveTotals = (totalsResultSettled: PromiseSettledResult<ReportsFunctionResponse>) => {
        const totalsValue = totalsResultSettled.status === 'fulfilled' ? totalsResultSettled.value : null;
        const totals = (totalsValue?.success && totalsValue?.code !== 'WORKER_RESOURCE_LIMIT')
          ? (totalsValue.totalsInvalidDueToTimeout ? null : (totalsValue.totalsData ?? null))
          : null;
        const totalsUnavailable = totalsValue === null
          || totalsValue?.success === false
          || totalsValue?.totalsInvalidDueToTimeout === true
          || totalsValue?.code === 'WORKER_RESOURCE_LIMIT';
        return { totals, totalsUnavailable };
      };

      // 120 × 5s = 10 min absolute ceiling while queued; 30 × 5s = 150s once processing starts
      pollUntilGenerated({
        timerRef: generationPollRef,
        intervalMs: 5000,
        maxQueuedPolls: 120,
        maxProcessingPolls: 30,
        isCurrent: () => currentRequestRef.current === requestId,
        onTick: nudgeCoordinatorOnPoll,
        poll: (pollNumber) => {
          console.log(`[useProgressiveReports] Single source poll #${pollNumber}`);
          return fetchSingleSourceBoth();
        },
        classify: ([pollDataResult]) => {
          const pollResp = pollDataResult.status === 'fulfilled' ? pollDataResult.value : null;
          // Request changed while poll was in-flight; ignore stale response
          if (currentRequestRef.current !== requestId) return { kind: 'ignore' };
          if (!pollResp?.success) return { kind: 'ignore' };
          if (pollResp.generating) {
            return { kind: 'generating', processing: pollResp.jobStatus === 'processing' };
          }
          return { kind: 'done' };
        },
        onGenerating: ([pollDataResult, pollTotalsResult]) => {
          const pollResp = pollDataResult.status === 'fulfilled' ? pollDataResult.value : null;
          const pollTotalsResp = pollTotalsResult.status === 'fulfilled' ? pollTotalsResult.value : null;
          const pollDataReason = pollResp?.generationReason ?? null;
          const pollTotalsReason = pollTotalsResp?.generationReason ?? null;
          const livePollReason: ReportGenerationReason | null = (
            pollDataReason === 'inventory_source_changed' || pollTotalsReason === 'inventory_source_changed'
          )
            ? 'inventory_source_changed'
            : (pollDataReason ?? pollTotalsReason ?? null);
          setReportState(prev => (
            prev.generationReason === livePollReason
              ? prev
              : { ...prev, generationReason: livePollReason }
          ));
        },
        onDone: ([pollDataResult, pollTotalsResult]) => {
          const pollResp = pollDataResult.status === 'fulfilled' ? pollDataResult.value : null;
          const pollData = pollResp?.data || [];
          const pollTotalRows = pollResp?.totalRows || 0;
          const { totals: pollTotals, totalsUnavailable: pollTotalsUnavailable } = resolveTotals(pollTotalsResult);

          setReportState({
            data: pollData,
            loading: false,
            generating: false,
            generationReason: null,
            loadingBySource: {},
            completedSources: [connId],
            error: null,
            totalRows: pollTotalRows,
            totalRowsKnown: true,
            totalsData: pollTotals,
            totalsUnavailable: pollTotalsUnavailable,
            warnings: [],
          });

          cacheRef.current.set(connId, {
            data: pollData,
            totals: pollTotals,
            rowCount: pollTotalRows,
            filters: filterKey,
          });
        },
        finalFetch: async () => {
          console.log('[useProgressiveReports] Single source: max polls reached, doing final fetch...');
          const [finalDataResult, finalTotalsResult] = await fetchSingleSourceBoth();
          const finalResp = finalDataResult.status === 'fulfilled' ? finalDataResult.value : null;
          if (!finalResp?.success || finalResp.generating) return false;

          console.log('[useProgressiveReports] Single source: final fetch completed');
          const { totals: finalTotals, totalsUnavailable: finalTotalsUnavailable } = resolveTotals(finalTotalsResult);
          setReportState({
            data: finalResp.data || [],
            loading: false,
            generating: false,
            generationReason: null,
            loadingBySource: {},
            completedSources: [connId],
            error: null,
            totalRows: finalResp.totalRows || 0,
            totalRowsKnown: true,
            totalsData: finalTotals,
            totalsUnavailable: finalTotalsUnavailable,
            warnings: [],
          });
          return true;
        },
        onTimeout: markGenerationTimedOut,
        onPollError: (pollError) => console.error('[useProgressiveReports] Single source poll error:', pollError),
        onFinalFetchError: (e) => console.warn('[useProgressiveReports] Single source: final fetch failed:', e),
      });

      return;
    }

    // Process totals result
    let singleSourceTotalsUnavailable = false;
    if (totalsResult.status === 'fulfilled') {
      const totalsValue = totalsResult.value;
      if (totalsValue?.success && totalsValue?.code !== 'WORKER_RESOURCE_LIMIT') {
        totalsData = totalsValue.totalsInvalidDueToTimeout ? null : (totalsValue.totalsData ?? null);
        if (totalsValue.totalsInvalidDueToTimeout) {
          singleSourceTotalsUnavailable = true;
        }
      } else {
        singleSourceTotalsUnavailable = true;
      }
    } else {
      singleSourceTotalsUnavailable = true;
    }

    // Combine newly fetched data with cached data (grouping done by backend)
    const allData = [...cachedData, ...data];

    // When column filters are applied, use actual data length as totalRows
    // since backend count doesn't reflect column filtering
    const hasColumnFilters = filters.columnFilters && filters.columnFilters.length > 0;

    // Use accurate count if available, otherwise use aggregated count
    // For single source, backend returns accurate totalRows; only use processedData.length for multi-source SKU reports
    const isSingleSource = cachedData.length === 0;
    const combinedTotalRows = hasColumnFilters
      ? allData.length  // Use actual filtered data length when filters are active
      : (accurateTotalCount !== null
        ? accurateTotalCount
        : (isSingleSource
          ? totalRows  // Use backend's totalRows for single source
          : totalRows + cachedRowCount));
    const combinedTotals = singleSourceTotalsUnavailable
      ? null
      : ((totalsData && cachedTotals.length > 0)
        ? aggregateMultiConnectionTotals([...cachedTotals, totalsData], filters)
        : (totalsData || (cachedTotals.length > 0 ? aggregateMultiConnectionTotals(cachedTotals, filters) : null)));

    // Cache the newly fetched data
    cacheRef.current.set(connId, {
      data,
      totals: totalsData,
      totalsInvalidDueToTimeout: totalsResult.status === 'fulfilled' && totalsResult.value?.success
        ? totalsResult.value.totalsInvalidDueToTimeout === true
        : false,
      rowCount: totalRows,
      filters: filterKey,
      inventorySourceApplied,
    });

    // Get all completed sources (newly fetched + cached)
    const completedSources = [connId];
    // Add any cached connection IDs
    cacheRef.current.forEach((_, key) => {
      if (key !== connId && cacheRef.current.get(key)?.filters === filterKey) {
        completedSources.push(key);
      }
    });

    const pageSize = filters.pageSize || 100;
    const limitedData = allData.slice(0, pageSize);

    setReportState({
      data: limitedData,
      loading: false,
      generating: false,
      generationReason: null,
      loadingBySource: {},
      completedSources,
      error: null,
      totalRows: combinedTotalRows,
      totalRowsKnown: isSingleSource || accurateTotalCount !== null || !isMultiSourceSkuReport,
      totalsData: combinedTotals,
      totalsUnavailable: singleSourceTotalsUnavailable,
      warnings: [],
      inventorySourceApplied,
    });

    onDataUpdate?.(limitedData, combinedTotalRows, combinedTotals);
  }

  return {
    ...reportState,
    fetchReportData
  };
};